import type { ApiResponse } from "../genesisApi/httpsMethods";
import type {
  AskResponse,
  BatchUploadResponse,
  CoverageSlice,
  DuplicateUploadDetail,
  MarketMetric,
  MarketReportDetail,
  MarketReportListItem,
  MarketViewDetail,
  MarketViewListItem,
  ProcessingJob,
  RefreshMarketViewRequest,
  SourceEvidence,
  UploadReportResponse,
} from "../../types/marketReport";
import {
  isMarketReportApiConfigured,
  marketReportApiGetUrl,
  marketReportApiHeaders,
  marketReportApiPostUrl,
} from "./marketReportApiRoot";

export { isMarketReportApiConfigured };

type FastApiDetail =
  | string
  | DuplicateUploadDetail
  | Array<{ loc: string[]; msg: string; type: string }>;

function parseDetailMessage(detail: FastApiDetail): string | DuplicateUploadDetail {
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map((e) => e.msg).join("; ");
  }
  return detail;
}

async function handleResponse<T>(
  response: Response,
  errorMessage: string,
): Promise<ApiResponse<T>> {
  const status = response.status;

  if (status === 204) {
    return {
      success: true,
      status: 204,
      data: null as unknown as T,
    };
  }

  try {
    const text = await response.text();
    const json = text ? JSON.parse(text) : null;

    if (!response.ok) {
      const detail = json?.detail;
      const message = detail !== undefined ? parseDetailMessage(detail) : `${errorMessage} (${status})`;
      return {
        success: false,
        status,
        message,
      };
    }

    return {
      success: true,
      status,
      data: json as T,
    };
  } catch {
    return {
      success: false,
      status,
      message: `Unexpected response format (${status})`,
    };
  }
}

type FetchOutcome =
  | { ok: true; response: Response }
  | { ok: false; failure: ApiResponse<never> };

/**
 * Builds the URL and performs the fetch, returning config/network errors as a
 * `status: 0` failure instead of rejecting (matching the genesisApi helpers).
 */
async function safeFetch(
  buildUrl: () => string,
  init: RequestInit,
  errorMessage: string,
): Promise<FetchOutcome> {
  try {
    const response = await fetch(buildUrl(), init);
    return { ok: true, response };
  } catch (error) {
    const reason =
      error instanceof Error && error.message ? error.message : "Network error";
    return {
      ok: false,
      failure: {
        success: false,
        status: 0,
        message: `${errorMessage}: ${reason}`,
      },
    };
  }
}

async function request<T>(
  buildUrl: () => string,
  init: RequestInit,
  errorMessage: string,
): Promise<ApiResponse<T>> {
  const outcome = await safeFetch(buildUrl, init, errorMessage);
  if (!outcome.ok) {
    return outcome.failure;
  }
  return handleResponse<T>(outcome.response, errorMessage);
}

export async function listReports(
  skip = 0,
  limit = 50,
): Promise<ApiResponse<MarketReportListItem[]>> {
  return request<MarketReportListItem[]>(
    () => `${marketReportApiGetUrl("reports")}?skip=${skip}&limit=${limit}`,
    { method: "GET", headers: marketReportApiHeaders() },
    "GET /reports failed",
  );
}

export async function getReport(
  reportId: string,
): Promise<ApiResponse<MarketReportDetail>> {
  return request<MarketReportDetail>(
    () => marketReportApiGetUrl(`reports/${reportId}`),
    { method: "GET", headers: marketReportApiHeaders() },
    "GET /reports/{id} failed",
  );
}

export async function getReportCoverage(
  reportId: string,
): Promise<ApiResponse<CoverageSlice[]>> {
  return request<CoverageSlice[]>(
    () => marketReportApiGetUrl(`reports/${reportId}/coverage`),
    { method: "GET", headers: marketReportApiHeaders() },
    "GET /reports/{id}/coverage failed",
  );
}

export async function getReportMetrics(
  reportId: string,
): Promise<ApiResponse<MarketMetric[]>> {
  return request<MarketMetric[]>(
    () => marketReportApiGetUrl(`reports/${reportId}/metrics`),
    { method: "GET", headers: marketReportApiHeaders() },
    "GET /reports/{id}/metrics failed",
  );
}

export async function getCoverageEvidence(
  sliceId: string,
): Promise<ApiResponse<SourceEvidence[]>> {
  return request<SourceEvidence[]>(
    () => marketReportApiGetUrl(`reports/coverage/${sliceId}/evidence`),
    { method: "GET", headers: marketReportApiHeaders() },
    "GET /reports/coverage/{slice_id}/evidence failed",
  );
}

export async function uploadReport(
  file: File,
): Promise<ApiResponse<UploadReportResponse>> {
  const formData = new FormData();
  formData.append("file", file);

  return request<UploadReportResponse>(
    () => marketReportApiPostUrl("reports/upload"),
    { method: "POST", headers: marketReportApiHeaders(false), body: formData },
    "POST /reports/upload failed",
  );
}

export async function uploadReportsBatch(
  files: File[],
): Promise<ApiResponse<BatchUploadResponse>> {
  const formData = new FormData();
  for (const file of files) {
    formData.append("files", file);
  }

  return request<BatchUploadResponse>(
    () => marketReportApiPostUrl("reports/upload/batch"),
    { method: "POST", headers: marketReportApiHeaders(false), body: formData },
    "POST /reports/upload/batch failed",
  );
}

export function parseContentDispositionFilename(
  header: string | null,
): string | null {
  if (!header) return null;
  const quoted = /filename\*?=(?:UTF-8''|utf-8'')?["']?([^"';]+)["']?/i.exec(
    header,
  );
  if (quoted?.[1]) {
    try {
      return decodeURIComponent(quoted[1]);
    } catch {
      return quoted[1];
    }
  }
  const simple = /filename="([^"]+)"/i.exec(header);
  if (simple?.[1]) return simple[1];
  const unquoted = /filename=([^;]+)/i.exec(header);
  if (unquoted?.[1]) return unquoted[1].trim();
  return null;
}

export async function downloadReport(
  reportId: string,
): Promise<ApiResponse<{ blob: Blob; filename: string }>> {
  const outcome = await safeFetch(
    () => marketReportApiGetUrl(`reports/${reportId}/download`),
    { method: "GET", headers: marketReportApiHeaders(false) },
    "GET /reports/{id}/download failed",
  );
  if (!outcome.ok) {
    return outcome.failure;
  }
  const response = outcome.response;

  if (!response.ok) {
    try {
      const text = await response.text();
      const json = text ? JSON.parse(text) : null;
      const detail = json?.detail;
      const message =
        typeof detail === "string"
          ? detail
          : `GET /reports/{id}/download failed (${response.status})`;
      return { success: false, status: response.status, message };
    } catch {
      return {
        success: false,
        status: response.status,
        message: `GET /reports/{id}/download failed (${response.status})`,
      };
    }
  }

  let blob: Blob;
  try {
    blob = await response.blob();
  } catch {
    return {
      success: false,
      status: response.status,
      message: `GET /reports/{id}/download failed (${response.status})`,
    };
  }
  const filename =
    parseContentDispositionFilename(
      response.headers.get("Content-Disposition"),
    ) ?? `report-${reportId}.pdf`;

  return {
    success: true,
    status: response.status,
    data: { blob, filename },
  };
}

export async function deleteReport(
  reportId: string,
): Promise<ApiResponse<null>> {
  return request<null>(
    () => marketReportApiGetUrl(`reports/${reportId}`),
    { method: "DELETE", headers: marketReportApiHeaders() },
    "DELETE /reports/{id} failed",
  );
}

export async function getJob(
  jobId: string,
): Promise<ApiResponse<ProcessingJob>> {
  return request<ProcessingJob>(
    () => marketReportApiGetUrl(`jobs/${jobId}`),
    { method: "GET", headers: marketReportApiHeaders() },
    "GET /jobs/{id} failed",
  );
}

export async function listMarketViews(
  skip = 0,
  limit = 50,
): Promise<ApiResponse<MarketViewListItem[]>> {
  return request<MarketViewListItem[]>(
    () => `${marketReportApiGetUrl("market-views")}?skip=${skip}&limit=${limit}`,
    { method: "GET", headers: marketReportApiHeaders() },
    "GET /market-views failed",
  );
}

export async function getMarketView(
  geography: string,
  sector: string,
): Promise<ApiResponse<MarketViewDetail>> {
  const geo = encodeURIComponent(geography);
  const sec = encodeURIComponent(sector);
  return request<MarketViewDetail>(
    () => marketReportApiGetUrl(`market-views/${geo}/${sec}`),
    { method: "GET", headers: marketReportApiHeaders() },
    "GET /market-views/{geography}/{sector} failed",
  );
}

export async function refreshMarketView(
  refreshRequest: RefreshMarketViewRequest,
): Promise<ApiResponse<MarketViewDetail>> {
  return request<MarketViewDetail>(
    () => marketReportApiPostUrl("market-views/refresh"),
    {
      method: "POST",
      headers: marketReportApiHeaders(),
      body: JSON.stringify({
        geography: refreshRequest.geography,
        sector: refreshRequest.sector,
        topics: refreshRequest.topics ?? null,
        view_type: refreshRequest.view_type ?? "sector_overview",
      }),
    },
    "POST /market-views/refresh failed",
  );
}

export async function askQuestion(
  question: string,
): Promise<ApiResponse<AskResponse>> {
  return request<AskResponse>(
    () => marketReportApiPostUrl("ask"),
    {
      method: "POST",
      headers: marketReportApiHeaders(),
      body: JSON.stringify({ question }),
    },
    "POST /ask failed",
  );
}

export function isDuplicateUploadError(
  message: ApiResponse<unknown>["message"],
): message is DuplicateUploadDetail {
  return (
    typeof message === "object" &&
    message !== null &&
    "report_id" in message &&
    "message" in message
  );
}
